
import React from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import DashboardCard from '@/components/common/DashboardCard';
import { CreditCard, DollarSign, TicketIcon, Users } from 'lucide-react';
import RoyaltyTrendsChart from '@/components/admin/dashboard/RoyaltyTrendsChart';
import RecentPaymentRequests from '@/components/admin/dashboard/RecentPaymentRequests';
import RecentSupportTickets from '@/components/admin/dashboard/RecentSupportTickets';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { usePaymentRequests } from '@/hooks/usePaymentRequests';
import { useSupportTickets } from '@/hooks/useSupportTickets';
import { useAuthors } from '@/hooks/useAuthors';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  // Enable idle timeout for automatic logout
  useIdleTimeout();

  // Fetch real data
  const { data: dashboardStats, isLoading: statsLoading } = useDashboardStats();
  const { data: paymentRequests } = usePaymentRequests();
  const { data: supportTickets } = useSupportTickets();
  const { data: authors } = useAuthors();

  // Get recent items
  const recentPayments = paymentRequests?.slice(0, 5) || [];
  const recentTickets = supportTickets?.slice(0, 5) || [];

  if (statsLoading) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <PageHeader 
          title="Admin Dashboard" 
          description="Overview of the royalty management system."
        />
        <div className="flex justify-center p-8">
          <p>Loading dashboard...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout allowedRoles={['admin']}>
      <PageHeader 
        title="Admin Dashboard" 
        description="Overview of the royalty management system."
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DashboardCard
          title="Total Authors"
          value={dashboardStats?.totalAuthors || 0}
          icon={<Users className="h-4 w-4" />}
          description="Active author accounts"
          onClick={() => navigate('/admin/authors')}
        />
        <DashboardCard
          title="Total Royalties"
          value={`$${(dashboardStats?.totalRoyalties || 0).toFixed(2)}`}
          icon={<DollarSign className="h-4 w-4" />}
          description="All time royalty payments"
          trend={dashboardStats?.trendPercentage ? (Number(dashboardStats.trendPercentage) >= 0 ? "up" : "down") : undefined}
          trendValue={dashboardStats?.trendPercentage ? `${Math.abs(Number(dashboardStats.trendPercentage))}% from last quarter` : undefined}
          onClick={() => navigate('/admin/royalties')}
        />
        <DashboardCard
          title="Pending Payments"
          value={dashboardStats?.pendingPayments || 0}
          icon={<CreditCard className="h-4 w-4" />}
          description="Payment requests to process"
          onClick={() => navigate('/admin/payments')}
        />
        <DashboardCard
          title="Open Tickets"
          value={dashboardStats?.openTickets || 0}
          icon={<TicketIcon className="h-4 w-4" />}
          description="Support tickets needing attention"
          onClick={() => navigate('/admin/support')}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <RoyaltyTrendsChart data={dashboardStats?.quarterlyTrends || []} />
        <RecentPaymentRequests 
          payments={recentPayments} 
          authors={authors || []} 
          onViewAll={() => navigate('/admin/payments')} 
        />
      </div>

      <RecentSupportTickets 
        tickets={recentTickets} 
        authors={authors || []} 
        onViewAll={() => navigate('/admin/support')} 
      />
    </MainLayout>
  );
};

export default AdminDashboard;
